import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, Clock, User, Phone, Loader2, CheckCircle2,
  AlertCircle, ArrowLeft, CalendarX, XCircle, ShieldCheck, KeyRound,
  MapPin, Wifi, SquareParking, Accessibility, MessageCircle, Tag, Check, DollarSign, Video, Cake,
  Banknote, QrCode, CreditCard, Wallet
} from 'lucide-react';
import { publicoService } from '../services/api';

const DIFERENCIAIS_DISPONIVEIS = [
  { chave: 'wifi', label: 'Wi-Fi', icone: Wifi },
  { chave: 'estacionamento', label: 'Estacionamento', icone: SquareParking },
  { chave: 'acessibilidade', label: 'Acessibilidade', icone: Accessibility },
  { chave: 'atendimento_online', label: 'Atendimento Online', icone: Video },
];

const METODOS_PAGAMENTO = [
  { chave: 'dinheiro', label: 'Dinheiro', icone: Banknote },
  { chave: 'pix', label: 'Pix', icone: QrCode },
  { chave: 'credito', label: 'Crédito', icone: CreditCard },
  { chave: 'debito', label: 'Débito', icone: Wallet },
];

const hojeInput = new Date().toLocaleDateString('en-CA');

const formatarPreco = (preco) => {
  if (preco === null || preco === undefined || preco === '') return null;
  return Number(preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const gerarLinkGoogleMaps = (endereco) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`;

const gerarLinkWhatsapp = (telefone) => {
  let tel = telefone.replace(/\D/g, '');
  if (tel.length === 10 || tel.length === 11) tel = `55${tel}`;
  return `https://wa.me/${tel}`;
};

const limparTelefone = (telefone) => {
  let tel = telefone.replace(/\D/g, '');
  if (tel.length === 10 || tel.length === 11) tel = `55${tel}`;
  return tel;
};

const AgendaPublica = () => {
  const { slug } = useParams();

  const [carregando, setCarregando] = useState(true);
  const [naoEncontrada, setNaoEncontrada] = useState(false);
  const [estabelecimento, setEstabelecimento] = useState(null);
  const [servicos, setServicos] = useState([]);

  const [modo, setModo] = useState('agendar'); // 'agendar' | 'cancelar'

  // --- Fluxo de agendamento ---
  const [servicosIds, setServicosIds] = useState([]);
  const [data, setData] = useState('');
  const [horarios, setHorarios] = useState([]);
  const [buscandoHorarios, setBuscandoHorarios] = useState(false);
  const [horario, setHorario] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmado, setConfirmado] = useState(null);

  // --- Cupom de desconto ---
  const [cupomCodigo, setCupomCodigo] = useState('');
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [erroCupom, setErroCupom] = useState('');

  // --- Forma de pagamento ---
  const [formaPagamento, setFormaPagamento] = useState('');

  // --- Verificação do WhatsApp por código ---
  const [codigoEtapa, setCodigoEtapa] = useState('idle'); // idle | enviado
  const [telefoneVerificado, setTelefoneVerificado] = useState(false);
  const [codigoInput, setCodigoInput] = useState('');
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  const [erroCodigo, setErroCodigo] = useState('');

  // --- Reconhecimento de cliente já cadastrado pelo telefone ---
  const [clienteExiste, setClienteExiste] = useState(null); // null=indefinido | true | false
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const debounceClienteRef = useRef(null);

  // --- Fluxo de cancelamento ---
  const [telefoneCancelar, setTelefoneCancelar] = useState('');
  const [buscandoAgendamentos, setBuscandoAgendamentos] = useState(false);
  const [agendamentos, setAgendamentos] = useState(null);
  const [cancelandoId, setCancelandoId] = useState(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        const [perfilRes, servicosRes] = await Promise.all([
          publicoService.obterPerfil(slug),
          publicoService.listarServicos(slug)
        ]);
        setEstabelecimento(perfilRes.data);
        setServicos(servicosRes.data || []);
      } catch (err) {
        setNaoEncontrada(true);
      } finally {
        setCarregando(false);
      }
    };
    carregar();
  }, [slug]);

  const buscarHorarios = useCallback(async () => {
    if (servicosIds.length === 0 || !data) return;
    setBuscandoHorarios(true);
    setHorario('');
    setHorarios([]);
    try {
      const res = await publicoService.buscarHorarios(slug, servicosIds, data);
      setHorarios(res.data || []);
    } catch (err) {
      setHorarios([]);
    } finally {
      setBuscandoHorarios(false);
    }
  }, [slug, servicosIds, data]);

  useEffect(() => { buscarHorarios(); }, [buscarHorarios]);

  // Trocar os serviços selecionados invalida um cupom já aplicado (o desconto foi calculado pra outra combinação).
  useEffect(() => {
    setCupomAplicado(null);
    setCupomCodigo('');
    setErroCupom('');
  }, [servicosIds]);

  const alternarServico = (id) => {
    setServicosIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const servicosSelecionados = servicos.filter(s => servicosIds.includes(s.id));
  const totalPreco = servicosSelecionados.reduce((soma, s) => soma + (s.preco ? Number(s.preco) : 0), 0);
  const totalDuracao = servicosSelecionados.reduce((soma, s) => soma + (s.duracao_minutos || 0), 0);

  const handleAplicarCupom = async () => {
    if (!cupomCodigo || servicosIds.length === 0) return;
    setValidandoCupom(true);
    setErroCupom('');
    try {
      const res = await publicoService.validarCupom(slug, { codigo: cupomCodigo, servicos_ids: servicosIds });
      setCupomAplicado(res.data);
    } catch (err) {
      setCupomAplicado(null);
      setErroCupom(err.response?.data?.erro || 'Cupom inválido.');
    } finally {
      setValidandoCupom(false);
    }
  };

  const handleTelefoneChange = (valor) => {
    setTelefone(valor);
    // Trocar o telefone invalida um código já enviado/verificado pro número anterior.
    setCodigoEtapa('idle');
    setTelefoneVerificado(false);
    setCodigoInput('');
    setErroCodigo('');

    setClienteExiste(null);
    setNome('');
    setDataNascimento('');
    clearTimeout(debounceClienteRef.current);

    const telefoneLimpo = limparTelefone(valor);
    if (telefoneLimpo.length < 12) return;

    setBuscandoCliente(true);
    debounceClienteRef.current = setTimeout(async () => {
      try {
        const res = await publicoService.buscarCliente(slug, telefoneLimpo);
        if (res.data?.nome) {
          setNome(res.data.nome);
          setClienteExiste(true);
        } else {
          setClienteExiste(false);
        }
      } catch (err) {
        setClienteExiste(null);
      } finally {
        setBuscandoCliente(false);
      }
    }, 500);
  };

  const handleEnviarCodigo = async () => {
    setErroCodigo('');
    setEnviandoCodigo(true);
    try {
      await publicoService.enviarCodigo(slug, { nome, telefone: limparTelefone(telefone) });
      setCodigoEtapa('enviado');
    } catch (err) {
      setErroCodigo(err.response?.data?.erro || 'Não foi possível enviar o código. Tente novamente.');
    } finally {
      setEnviandoCodigo(false);
    }
  };

  const handleVerificarCodigo = async () => {
    setErroCodigo('');
    setVerificandoCodigo(true);
    try {
      await publicoService.verificarCodigo(slug, { telefone: limparTelefone(telefone), codigo: codigoInput });
      setTelefoneVerificado(true);
    } catch (err) {
      setErroCodigo(err.response?.data?.erro || 'Código inválido ou expirado.');
    } finally {
      setVerificandoCodigo(false);
    }
  };

  const handleAgendar = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      await publicoService.agendar(slug, {
        nome,
        telefone: limparTelefone(telefone),
        servicos_ids: servicosIds,
        data,
        hora: horario,
        cupom_codigo: cupomAplicado ? cupomCodigo : undefined,
        data_nascimento: clienteExiste === false && dataNascimento ? dataNascimento : undefined,
        forma_pagamento: formaPagamento
      });
      setConfirmado({ servico: servicosSelecionados.map(s => s.nome_servico).join(', '), data, horario });
    } catch (err) {
      const msg = err.response?.data?.erro || 'Não foi possível concluir o agendamento. Tente novamente.';
      setErro(msg);
      if (err.response?.status === 409) buscarHorarios();
    } finally {
      setEnviando(false);
    }
  };

  const reiniciarFluxo = () => {
    setConfirmado(null);
    setServicosIds([]);
    setData('');
    setHorario('');
    setHorarios([]);
    setNome('');
    setTelefone('');
    setDataNascimento('');
    setErro('');
    setCodigoEtapa('idle');
    setTelefoneVerificado(false);
    setCodigoInput('');
    setErroCodigo('');
    setClienteExiste(null);
    setCupomCodigo('');
    setCupomAplicado(null);
    setErroCupom('');
    setFormaPagamento('');
  };

  const buscarMeusAgendamentos = async (e) => {
    e.preventDefault();
    setBuscandoAgendamentos(true);
    setAgendamentos(null);
    try {
      const res = await publicoService.meusAgendamentos(slug, limparTelefone(telefoneCancelar));
      setAgendamentos(res.data || []);
    } catch (err) {
      setAgendamentos([]);
    } finally {
      setBuscandoAgendamentos(false);
    }
  };

  const handleCancelar = async (consultaId) => {
    if (!window.confirm('Confirmar cancelamento deste agendamento?')) return;
    setCancelandoId(consultaId);
    try {
      await publicoService.cancelar(slug, { telefone: limparTelefone(telefoneCancelar), consulta_id: consultaId });
      setAgendamentos(prev => prev.filter(a => a.id !== consultaId));
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao cancelar o agendamento.');
    } finally {
      setCancelandoId(null);
    }
  };

  if (carregando) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[var(--cor-fundo)] to-white">
        <Loader2 className="animate-spin text-[var(--cor-primaria)]" size={40} />
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Carregando agenda...</p>
      </div>
    );
  }

  if (naoEncontrada) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[var(--cor-fundo)] to-white p-6 text-center">
        <CalendarX className="text-slate-300" size={48} />
        <h1 className="text-lg font-black uppercase text-slate-700">Agenda não encontrada</h1>
        <p className="text-sm text-slate-400 max-w-sm">Verifique se o link está correto ou entre em contato com o estabelecimento.</p>
      </div>
    );
  }

  const tema = estabelecimento.tema || {};
  const temaVars = {
    '--cor-primaria': tema.corPrimaria || '#1883FF',
    '--cor-secundaria': tema.corSecundaria || '#6D28D9',
    '--cor-fundo': tema.corFundo || '#F8F7FF',
  };

  // O cartão de verificação por código já traz seu próprio botão de ação — enquanto ele estiver
  // visível, escondemos o botão final pra não duplicar a chamada de ação na tela.
  const mostrandoVerificacao = !telefoneVerificado && !!nome && !!telefone;

  const formasPagamento = estabelecimento.formas_pagamento || {};
  const metodosPagamentoDisponiveis = METODOS_PAGAMENTO.filter(({ chave }) => {
    if (chave === 'dinheiro' || chave === 'pix') return !!formasPagamento[chave];
    return !!formasPagamento[chave]?.ativo;
  });
  // 'somar': a taxa é acrescida no preço que o cliente paga. 'descontar': o preço do cliente não
  // muda — quem absorve a taxa é o profissional (só reflete no valor líquido registrado por lá).
  const calcularValorComForma = (precoBase, forma) => {
    if (forma === 'dinheiro' || forma === 'pix') return precoBase;
    const cfg = formasPagamento[forma];
    if (!cfg?.ativo) return precoBase;
    const taxa = Number(cfg.taxa) || 0;
    return cfg.modo === 'descontar' ? precoBase : precoBase * (1 + taxa / 100);
  };
  const precoBaseCliente = cupomAplicado ? cupomAplicado.precoComDesconto : totalPreco;
  const precoFinalComForma = formaPagamento ? calcularValorComForma(precoBaseCliente, formaPagamento) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--cor-fundo)] to-white py-8 px-4 sm:py-12" style={temaVars}>
      <div className="w-full max-w-[520px] mx-auto">

        <div className="text-center mb-8">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--cor-secundaria)] mb-1">Agenda Sincro</p>
          <h1 className="text-2xl sm:text-3xl font-black uppercase italic text-slate-900 m-0">{estabelecimento.nome}</h1>

          {(estabelecimento.endereco || estabelecimento.telefone) && (
            <div className="flex flex-col items-center gap-1 mt-2">
              {estabelecimento.endereco && (
                <a
                  href={gerarLinkGoogleMaps(estabelecimento.endereco)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[var(--cor-primaria)] hover:underline transition-colors"
                >
                  <MapPin size={13} className="text-[var(--cor-primaria)] shrink-0" /> {estabelecimento.endereco}
                </a>
              )}
              {estabelecimento.telefone && (
                <a
                  href={gerarLinkWhatsapp(estabelecimento.telefone)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[var(--cor-primaria)] hover:underline transition-colors"
                >
                  <MessageCircle size={13} className="text-[var(--cor-primaria)] shrink-0" /> {estabelecimento.telefone}
                </a>
              )}
            </div>
          )}

          {estabelecimento.diferenciais?.length > 0 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              {DIFERENCIAIS_DISPONIVEIS.filter(d => estabelecimento.diferenciais.includes(d.chave)).map(({ chave, label, icone: Icone }) => (
                <span
                  key={chave}
                  title={label}
                  className="flex items-center gap-1.5 bg-white border border-slate-100 shadow-sm rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-[var(--cor-secundaria)]"
                >
                  <Icone size={14} /> {label}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>

          {/* ALTERNADOR AGENDAR / CANCELAR */}
          {!confirmado && (
            <div className="flex bg-white shadow-sm border border-slate-100 rounded-2xl p-1.5 mb-8 gap-1">
              <button
                type="button"
                onClick={() => setModo('agendar')}
                className={`flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider border-none cursor-pointer transition-all ${modo === 'agendar' ? 'bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white shadow-sm' : 'bg-transparent text-slate-400'}`}
              >
                Agendar
              </button>
              <button
                type="button"
                onClick={() => setModo('cancelar')}
                className={`flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider border-none cursor-pointer transition-all ${modo === 'cancelar' ? 'bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white shadow-sm' : 'bg-transparent text-slate-400'}`}
              >
                Cancelar Agendamento
              </button>
            </div>
          )}

          {modo === 'agendar' && (
            confirmado ? (
              <div className="flex flex-col items-center text-center gap-4 py-4">
                <div className="bg-emerald-50 text-emerald-600 rounded-full p-4">
                  <CheckCircle2 size={36} />
                </div>
                <h2 className="text-lg font-black uppercase text-slate-800 m-0">Agendamento confirmado!</h2>
                <p className="text-sm text-slate-500 m-0">
                  <strong>{confirmado.servico}</strong> — {new Date(`${confirmado.data}T00:00:00`).toLocaleDateString('pt-BR')} às {confirmado.horario}
                </p>
                <button
                  onClick={reiniciarFluxo}
                  className="mt-2 bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-none px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider cursor-pointer hover:opacity-90 transition-all"
                >
                  Fazer novo agendamento
                </button>
              </div>
            ) : (
              <form onSubmit={handleAgendar} className="flex flex-col gap-5">

                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Serviços (escolha um ou mais)</label>
                  <div className="flex flex-col gap-2">
                    {servicos.map(s => {
                      const marcado = servicosIds.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => alternarServico(s.id)}
                          className={`flex items-center justify-between gap-3 text-left border-2 rounded-xl px-4 py-3 cursor-pointer transition-all shadow-sm ${marcado ? 'bg-[var(--cor-fundo)] border-[var(--cor-secundaria)]' : 'bg-white border-transparent hover:border-[var(--cor-secundaria)]/30'}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${marcado ? 'bg-[var(--cor-secundaria)] border-[var(--cor-secundaria)]' : 'border-slate-300'}`}>
                              {marcado && <Check size={13} className="text-white" strokeWidth={3} />}
                            </span>
                            <span className="text-sm font-bold text-slate-800 truncate">{s.nome_servico}</span>
                          </div>
                          <span className="shrink-0 text-xs font-black text-[var(--cor-secundaria)]">{formatarPreco(s.preco) || ''}</span>
                        </button>
                      );
                    })}
                  </div>
                  {servicos.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1.5"><AlertCircle size={14} /> Nenhum serviço disponível no momento.</p>
                  )}
                  {servicosIds.length > 0 && (
                    <div className="flex items-center justify-between gap-3 bg-white shadow-sm border border-slate-100 rounded-xl px-4 py-2.5 mt-1">
                      <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1.5"><Clock size={12} /> {totalDuracao} min</span>
                      <span className="text-sm font-black text-slate-800 flex items-center gap-1.5"><DollarSign size={14} className="text-[var(--cor-secundaria)]" /> {formatarPreco(totalPreco) || 'Grátis'}</span>
                    </div>
                  )}
                </div>

                {servicosIds.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1 flex items-center gap-1.5"><Tag size={12} /> Tem um cupom?</label>
                    {cupomAplicado ? (
                      <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Check size={16} />
                          <div>
                            <p className="m-0 text-xs font-black uppercase">{cupomCodigo} aplicado</p>
                            <p className="m-0 text-xs">
                              <span className="line-through text-emerald-500/60 mr-1.5">{formatarPreco(cupomAplicado.precoOriginal)}</span>
                              <strong>{formatarPreco(cupomAplicado.precoComDesconto)}</strong>
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setCupomAplicado(null); setCupomCodigo(''); }}
                          className="text-emerald-600 bg-white border-none px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase cursor-pointer hover:bg-emerald-100 transition-colors"
                        >
                          Remover
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={cupomCodigo}
                          onChange={e => setCupomCodigo(e.target.value.toUpperCase())}
                          placeholder="Ex: VERAO20"
                          className="flex-1 bg-white shadow-sm border border-purple-50 py-3 px-4 rounded-xl text-sm font-bold text-slate-800 uppercase outline-none transition-all focus:border-[var(--cor-secundaria)] focus:ring-4 focus:ring-[var(--cor-secundaria)]/10"
                        />
                        <button
                          type="button"
                          onClick={handleAplicarCupom}
                          disabled={!cupomCodigo || validandoCupom}
                          className="bg-white border border-purple-100 text-[var(--cor-secundaria)] px-5 rounded-xl text-[11px] font-black uppercase tracking-wider cursor-pointer hover:bg-[#F5F3FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validandoCupom ? <Loader2 className="animate-spin" size={16} /> : 'Aplicar'}
                        </button>
                      </div>
                    )}
                    {erroCupom && (
                      <p className="text-[11px] text-red-600 flex items-center gap-1.5 m-0"><AlertCircle size={12} /> {erroCupom}</p>
                    )}
                  </div>
                )}

                {servicosIds.length > 0 && metodosPagamentoDisponiveis.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Forma de pagamento</label>
                    <div className="grid grid-cols-2 gap-2">
                      {metodosPagamentoDisponiveis.map(({ chave, label, icone: Icone }) => {
                        const selecionado = formaPagamento === chave;
                        return (
                          <button
                            key={chave}
                            type="button"
                            onClick={() => setFormaPagamento(chave)}
                            className={`flex items-center gap-2 justify-center border-2 rounded-xl px-3 py-3 text-xs font-black cursor-pointer transition-all shadow-sm ${selecionado ? 'bg-[var(--cor-fundo)] border-[var(--cor-secundaria)] text-[var(--cor-secundaria)]' : 'bg-white border-transparent text-slate-500 hover:border-[var(--cor-secundaria)]/30'}`}
                          >
                            <Icone size={16} /> {label}
                          </button>
                        );
                      })}
                    </div>
                    {formaPagamento && precoFinalComForma !== null && (
                      <p className="text-[11px] text-slate-500 font-semibold ml-1 mt-1">
                        Total a pagar: <strong className="text-slate-800">{formatarPreco(precoFinalComForma) || 'Grátis'}</strong>
                        {(formaPagamento === 'credito' || formaPagamento === 'debito') && formasPagamento[formaPagamento]?.modo === 'somar' && Number(formasPagamento[formaPagamento]?.taxa) > 0 && ' (já com a taxa do cartão)'}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Data</label>
                  <input
                    type="date"
                    required
                    min={hojeInput}
                    value={data}
                    onChange={e => setData(e.target.value)}
                    className="w-full bg-white shadow-sm border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:border-[var(--cor-secundaria)] focus:ring-4 focus:ring-[var(--cor-secundaria)]/10"
                  />
                </div>

                {servicosIds.length > 0 && data && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Horário</label>
                    {buscandoHorarios ? (
                      <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold py-3">
                        <Loader2 className="animate-spin" size={16} /> Buscando horários livres...
                      </div>
                    ) : horarios.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-2">Nenhum horário disponível nessa data. Tente outra data.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {horarios.map(h => (
                          <button
                            type="button"
                            key={h}
                            onClick={() => setHorario(h)}
                            className={`py-2.5 rounded-xl text-xs font-black border cursor-pointer transition-all ${horario === h ? 'bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-transparent shadow-sm' : 'bg-white shadow-sm text-slate-700 border-purple-50 hover:border-[var(--cor-secundaria)]/30'}`}
                          >
                            {h}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {horario && (
                  <>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Seu WhatsApp</label>
                      <div className="flex items-center gap-3 bg-white shadow-sm border border-purple-50 rounded-xl px-4">
                        <Phone size={18} className="text-slate-400" />
                        <input
                          type="text"
                          required
                          disabled={telefoneVerificado}
                          value={telefone}
                          onChange={e => handleTelefoneChange(e.target.value)}
                          placeholder="Ex: 85988887766"
                          className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800 disabled:opacity-60"
                        />
                        {telefoneVerificado ? (
                          <ShieldCheck size={18} className="text-emerald-500 shrink-0" />
                        ) : buscandoCliente ? (
                          <Loader2 size={18} className="animate-spin text-slate-400 shrink-0" />
                        ) : null}
                      </div>
                    </div>

                    {clienteExiste === true ? (
                      <p className="text-[11px] text-emerald-600 font-bold ml-1 m-0 flex items-center gap-1.5">
                        <CheckCircle2 size={14} className="shrink-0" /> Bem-vindo(a) de volta, {nome}!
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Seu nome</label>
                        <div className="flex items-center gap-3 bg-white shadow-sm border border-purple-50 rounded-xl px-4">
                          <User size={18} className="text-slate-400" />
                          <input
                            type="text"
                            required
                            disabled={telefoneVerificado}
                            value={nome}
                            onChange={e => setNome(e.target.value)}
                            placeholder="Nome completo"
                            className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800 disabled:opacity-60"
                          />
                        </div>
                        {clienteExiste === false && (
                          <p className="text-[10px] text-slate-400 font-semibold ml-1 m-0">Primeira vez por aqui? É só preencher seu nome.</p>
                        )}
                      </div>
                    )}

                    {clienteExiste === false && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Data de aniversário</label>
                        <div className="flex items-center gap-3 bg-white shadow-sm border border-purple-50 rounded-xl px-4">
                          <Cake size={18} className="text-slate-400 shrink-0" />
                          <input
                            type="date"
                            max={hojeInput}
                            value={dataNascimento}
                            onChange={e => setDataNascimento(e.target.value)}
                            className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800"
                          />
                        </div>
                        <p className="text-[10px] text-slate-400 font-semibold ml-1 m-0">Opcional — usamos pra te mandar uma mensagem especial no seu aniversário.</p>
                      </div>
                    )}

                    {/* VERIFICAÇÃO DO WHATSAPP POR CÓDIGO */}
                    {mostrandoVerificacao && (
                      <div className="flex flex-col gap-2 bg-white shadow-sm border border-purple-50 rounded-xl p-4">
                        {codigoEtapa === 'idle' ? (
                          <button
                            type="button"
                            onClick={handleEnviarCodigo}
                            disabled={enviandoCodigo}
                            className="w-full bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-none py-4 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-[var(--cor-secundaria)]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                          >
                            {enviandoCodigo ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                            {enviandoCodigo ? 'Enviando...' : 'Confirmar seu agendamento por WhatsApp'}
                          </button>
                        ) : (
                          <>
                            <p className="text-[11px] text-slate-500 font-semibold m-0">
                              Enviamos um código pelo WhatsApp para {telefone}. Digite abaixo:
                            </p>
                            <div className="flex gap-2">
                              <div className="flex-1 flex items-center gap-2 bg-white border border-purple-100 rounded-lg px-3">
                                <KeyRound size={16} className="text-slate-400" />
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={6}
                                  value={codigoInput}
                                  onChange={e => setCodigoInput(e.target.value.replace(/\D/g, ''))}
                                  placeholder="000000"
                                  className="w-full py-2.5 bg-transparent border-none outline-none text-sm font-bold tracking-widest text-slate-800"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={handleVerificarCodigo}
                                disabled={verificandoCodigo || codigoInput.length < 6}
                                className="bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-none px-4 rounded-lg text-[10px] font-black uppercase tracking-wider cursor-pointer hover:opacity-90 transition-all disabled:opacity-50"
                              >
                                {verificandoCodigo ? <Loader2 className="animate-spin" size={14} /> : 'Verificar'}
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={handleEnviarCodigo}
                              disabled={enviandoCodigo}
                              className="self-start text-[10px] font-bold text-[var(--cor-secundaria)] bg-transparent border-none cursor-pointer underline disabled:opacity-50"
                            >
                              Reenviar código
                            </button>
                          </>
                        )}
                        {erroCodigo && (
                          <p className="text-[11px] text-red-600 flex items-center gap-1.5 m-0"><AlertCircle size={12} /> {erroCodigo}</p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {erro && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2"><AlertCircle size={14} /> {erro}</p>
                )}

                {!mostrandoVerificacao && (
                <button
                  type="submit"
                  disabled={!horario || !telefoneVerificado || !formaPagamento || enviando}
                  className="w-full bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-none py-4 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-[var(--cor-secundaria)]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {enviando ? <><Loader2 className="animate-spin" size={16} /> Confirmando...</> : !telefoneVerificado ? 'Confirme seu WhatsApp para agendar' : 'Confirmar Agendamento'}
                </button>
                )}
              </form>
            )
          )}

          {modo === 'cancelar' && (
            <div className="flex flex-col gap-5">
              <form onSubmit={buscarMeusAgendamentos} className="flex flex-col gap-3">
                <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Informe seu WhatsApp</label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-3 bg-white shadow-sm border border-purple-50 rounded-xl px-4">
                    <Phone size={18} className="text-slate-400" />
                    <input
                      type="text"
                      required
                      value={telefoneCancelar}
                      onChange={e => setTelefoneCancelar(e.target.value)}
                      placeholder="Ex: 85988887766"
                      className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={buscandoAgendamentos}
                    className="bg-gradient-to-r from-[var(--cor-primaria)] to-[var(--cor-secundaria)] text-white border-none px-5 rounded-xl text-[11px] font-black uppercase tracking-wider cursor-pointer hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {buscandoAgendamentos ? <Loader2 className="animate-spin" size={16} /> : 'Buscar'}
                  </button>
                </div>
              </form>

              {agendamentos !== null && (
                agendamentos.length === 0 ? (
                  <p className="text-xs text-slate-400 italic text-center py-6">Nenhum agendamento futuro encontrado para esse telefone.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {agendamentos.map(a => (
                      <div key={a.id} className="flex items-center justify-between gap-3 bg-white shadow-sm border border-purple-50 rounded-2xl p-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <Calendar size={16} className="text-[var(--cor-secundaria)] shrink-0" />
                          <div className="min-w-0">
                            <p className="m-0 text-sm font-bold text-slate-800">
                              {new Date(a.data_inicio).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="m-0 text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {new Date(a.data_inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleCancelar(a.id)}
                          disabled={cancelandoId === a.id}
                          className="shrink-0 text-red-500 bg-red-50 border-none px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wide cursor-pointer hover:bg-red-100 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {cancelandoId === a.id ? <Loader2 className="animate-spin" size={14} /> : <XCircle size={14} />} Cancelar
                        </button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest mt-6 flex items-center justify-center gap-1.5">
          <ArrowLeft size={12} /> Powered by Agenda Sincro
        </p>
      </div>
    </div>
  );
};

export default AgendaPublica;
